import React from "react";
import he from "he";
import { Map } from "immutable";
import { getVariable, varRegexp } from "services/records/variables";

export const escapeHTML = he.escape;

export const unescapeHTML = he.unescape;

export const stripVariables = (str = "") => str.replace(/{[a-f0-9]*\|.*}/, "");

export const isValidJSONString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }

  return true;
};

// This regex escapes all special characters for highlighting
export const escapeRegexp = (string = "") => string.replace(/[-[\]{}()*+?.,\\^$#\s]/g, "\\$&");

/**
 * Returns a copy of the selected response's description with variable values
 * substituted in
 *
 * @param {String} description
 * @param {Immutable.Map} variables
 * @returns {String}
 */
export const replaceVariableID = (description, variables) => {
  let newDescription = description;
  let result;
  const varRegex = new RegExp(varRegexp.source, "gi");
  result = varRegex.exec(description);

  while (result) {
    const match = result[0];
    const varID = result[1];
    const variable = getVariable(varID, variables, false, true);

    if (variable) {
      const varName = `<span class='g-variable'>${escapeHTML(variable.getIn(["name"]))}</span>`;
      newDescription = newDescription.replace(match, varName);
    } else {
      newDescription = newDescription.replace(match, "");
    }

    result = varRegex.exec(description);
  }

  return newDescription;
};

/**
 * Returns a copy of the selected response's description with variable values
 * substituted in
 *
 * @param {String} description
 * @param {Immutable.Map} variables
 * @returns {String}
 */
export const replaceVariableIDJSX = (description, variables) => {
  let matchResult;
  let resJSX = [];
  const varRegex = new RegExp(varRegexp.source, "gi");
  matchResult = varRegex.exec(description);
  let startingMatchIndex = 0;

  if (!matchResult) {
    return [description];
  }

  while (matchResult) {
    const { index } = matchResult;
    const match = matchResult[0];
    const varID = matchResult[1];
    const variable = getVariable(varID, variables, false, true);

    if (variable) {
      resJSX = resJSX
        .concat(description.slice(startingMatchIndex, index))
        .concat(
          <span className="g-variable">${variable.getIn(["name"])}</span>
        );
    } else {
      resJSX = resJSX
        .concat(description.slice(startingMatchIndex, index))
        .concat(match);
    }

    startingMatchIndex = index + match.length;

    matchResult = varRegex.exec(description);
  }

  resJSX = resJSX
    .concat(description.slice(startingMatchIndex));

  return resJSX;
};

/**
 * Renders a highlighted string based on a needle
 * @param {*} needle
 * @param {*} description
 */
export const renderHighlightedStringFromNeedle = (needle = "", description = "") => {
  if (!needle || !description) {
    return { __html: description };
  }

  const regexNeedle = needle.trim().replace(/\s+/g, "|");
  const escapedRegexNeedle = escapeRegexp(regexNeedle);
  const highlightMatchRegex = new RegExp(escapedRegexNeedle, "gi");
  const unescapedDescription = unescapeHTML(description);
  const hasMatch = highlightMatchRegex.test(unescapedDescription);

  if (hasMatch) {
    return {
      __html: unescapedDescription.replace(
        highlightMatchRegex,
        match => `<span class="g-highlight">${escapeHTML(match)}</span>`
      )
    };
  }

  return { __html: description };
};

/**
 * Highlight the indexes in string from locations
 * @param {String} string the complete string we are highlighting from
 * @param {List<Map>} locations [{start: number, end: number}, ...]
 * @param {Map} variables
 * @returns {{ _html: * }}
 */
export const renderHighlightedStringFromLocations = (
  string,
  locations,
  variables = Map()
) => {
  if (locations.size < 1) {
    return { __html: string };
  }

  // Array representation of our string
  let arrayString = string.split("").map(escapeHTML);

  locations.forEach((startEnd) => {
    arrayString[startEnd.get("start")] = `<span class="g-highlight">${arrayString[startEnd.get("start")]}`;
    arrayString[startEnd.get("end")] = `${arrayString[startEnd.get("end")]}</span>`;
  });

  // reconstruct our string
  arrayString = arrayString.join("");

  return { __html: replaceVariableID(arrayString, variables) };
};

export const camelCaseToSnakeCase = str => str.replace(/([A-Z])/g, g => `_${g[0].toLowerCase()}`);

/**
 * @param {String} str
 * @returns {String}
 */
export function removeTemplateVariables(str) {
  return str.replace(/{([\w]{24})\|(.*?)}/g, "");
}

/**
 * @param {String} str
 * @returns {Boolean}
 */
export function onlyWhiteSpace(str) {
  return !/\S/.test(str);
}

/**
 * returns a capitalized version of the string
 * @param {String} str
 * @returns {String}
 */
export function capitalizeFirstLetter(str = "") {
  if (typeof str !== "string") {
    throw new TypeError("Type of parameter str is not a string");
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * validateResponseMessageURL
 * return true if the url is a full, complete url string, or, starts with a variable.
 *
 * @param {String} url
 * @returns {Boolean}
 */
export const validateResponseMessageURL = (url = "") => {
  const linkWithTemplateVariablesRegex = /((([a-z]+:\/\/.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b)|^({([\w]{24})\|(.*?)}))([-a-zA-Z0-9@:%_+.~#?&/=]*)/;

  const linkPattern = new RegExp(
    [
      "(^([a-z]+:(\\/\\/)?)", // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,})|", // domain name and extension
      "((\\d{1,3}\\.){3}\\d{1,3}))", // OR ip (v4) address
      "(\\:\\d+)?", // port
      "(\\/[-a-z\\d%@_.~+&:]*)*", // path
      "(\\?[;&a-z\\d%@_.,~+&:=-]*)?", // query string
      "(\\#[-a-z\\d_]*)?$"
    ].join(""),
    "i"
  );

  const emailUrlPattern = new RegExp(
    [
      "^(mailto:)((([^<>()\\[\\]\\.,;:\\s@\"]",
      "+(\\.[^<>()\\[\\]\\.,;:\\s@\"]+)*)|(\".+\"))@",
      "((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|",
      "(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))",
      // eslint-disable-next-line no-useless-escape
      "|{(\w{24})|([^}]+)?})$" // allow variables
    ].join(""),
    "i"
  );

  const telUrlPattern = new RegExp(
    [
      "^(tel:)",
      "(\\\\?)",
      "(\\*?)",
      "(\\+?)",
      // eslint-disable-next-line no-useless-escape
      "([0-9]{3,15}|{(\w{24})|([^}]+)?})", // can be a number or a variable
      "$"
    ].join(""),
    "i"
  );

  const customProtocol = new RegExp("^([a-z]+)://", "i");

  const noJS = new RegExp("^(javascript:)", "i");

  return (
    !noJS.test(url) &&
    (linkWithTemplateVariablesRegex.test(url) ||
      linkPattern.test(url) ||
      emailUrlPattern.test(url) ||
      telUrlPattern.test(url) ||
      customProtocol.test(url))
  );
};

/**
 * Returns str with the trailing slashes removed, if any are present
 *
 * @param {String} str
 *
 * @returns {String}
 */
export function trimTrailingSlashes(str) {
  return str.replace(/\/+$/g, "");
}

/**
 * If a string has a length greater than maxNameLength * 1.1, it's truncated at
 * maxNameLength or the first instance of whitespace between maxNameLength and maxNameLength * 1.1
 *
 * If a string has a length between maxNameLength and maxNameLength * 1.1, it's
 * truncated at the full length or first instance of whitespace between
 * maxNameLength and maxNameLength * 1.1
 *
 * If a string has a length of maxNameLength or less than maxNameLength, it's not truncated
 *
 * @param {String} name
 * @param {Number} maxNameLength
 * @returns {String}
 */
export function truncateLongNames(name, maxNameLength) {
  if (name.length > maxNameLength) {
    let indexPositionToTruncateAt = name.indexOf(" ", maxNameLength);

    if (indexPositionToTruncateAt === -1 ||
      indexPositionToTruncateAt > Math.round(maxNameLength * 0.1) + maxNameLength) {
      indexPositionToTruncateAt = maxNameLength;
    }

    const nameToRenderTruncated = name.slice(0, indexPositionToTruncateAt).trimRight();

    return `${nameToRenderTruncated}...`;
  }

  return name;
}

/**
 * @param {String} text
 * @returns {String}
 */
export function applyRedactionFont(text) {
  const repeatCharacterRegex = /•/g;
  const repeatCharactersRegex = /•{3,}/g;
  const possibleCharacters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  // Test to see if there is a redaction
  const redacted = repeatCharactersRegex.test(text);

  // if no redactions, do not apply the font
  if (!redacted) {
    return text;
  }

  const redactionReplacement = () => {
    const randomCharacter = possibleCharacters[
      Math.floor(Math.random() * possibleCharacters.length)
    ];

    return `<span style="font-family: redacted;">${randomCharacter}</span>`;
  };

  return text.replace(repeatCharacterRegex, redactionReplacement);
}
